export type TranscriptLine = {
  id: string
  text: string
  isAction: boolean
}

export type DerivedTask = {
  id: string
  /** Must reference a TranscriptLine with isAction: true. */
  fromLineId: string
  title: string
  assignee: string
  due: string
  priority: 'high' | 'medium' | 'low'
  list: string
}

export const sampleTranscript: TranscriptLine[] = [
  { id: 'l1', text: 'Right, that’s everyone. Let’s keep this to fifteen minutes.', isAction: false },
  { id: 'l2', text: 'Quick one before we start — the staging box was down this morning, it’s back now.', isAction: false },
  { id: 'l3', text: 'Sarah, can you get the onboarding wireframes over to Dan by Friday?', isAction: true },
  { id: 'l4', text: 'Yep, Friday works. I’ll share the Figma link when they’re ready.', isAction: false },
  { id: 'l5', text: 'Dan’s going to need the copy too, but that can come after.', isAction: false },
  { id: 'l6', text: 'We still have that checkout bug from last week — it’s blocking two customers.', isAction: false },
  { id: 'l7', text: 'Miguel, take the checkout bug, that one’s urgent.', isAction: true },
  { id: 'l8', text: 'On it. I think it’s the currency rounding again.', isAction: false },
  { id: 'l9', text: 'Also the pricing page still says the old tiers.', isAction: false },
  { id: 'l10', text: 'Priya, update the pricing page copy before the launch on the 14th.', isAction: true },
  { id: 'l11', text: 'Sure. I’ll check with finance on the middle tier first.', isAction: false },
  { id: 'l12', text: 'Last thing — someone needs to write the migration notes for the API change.', isAction: false },
  { id: 'l13', text: 'Dan, can you draft the API migration notes by end of month?', isAction: true },
  { id: 'l14', text: 'Fine. Low priority though, nobody’s on v2 yet.', isAction: false },
  { id: 'l15', text: 'Agreed. That’s us — thanks everyone.', isAction: false },
]

export const derivedTasks: DerivedTask[] = [
  {
    id: 't1',
    fromLineId: 'l3',
    title: 'Send onboarding wireframes to Dan',
    assignee: 'Sarah',
    due: 'Fri 31 Jul',
    priority: 'medium',
    list: 'To Do',
  },
  {
    id: 't2',
    fromLineId: 'l7',
    title: 'Fix checkout currency rounding bug',
    assignee: 'Miguel',
    due: 'Today',
    priority: 'high',
    list: 'To Do',
  },
  {
    id: 't3',
    fromLineId: 'l10',
    title: 'Update pricing page copy for new tiers',
    assignee: 'Priya',
    due: 'Mon 14 Aug',
    priority: 'high',
    list: 'To Do',
  },
  {
    id: 't4',
    fromLineId: 'l13',
    title: 'Draft API migration notes',
    assignee: 'Dan',
    due: 'Thu 31 Aug',
    priority: 'low',
    list: 'To Do',
  },
]

/**
 * The animation only convinces if each card visibly comes from a line the
 * viewer can still read. This keeps the two lists honest.
 */
export function validateFixtures(
  lines: TranscriptLine[],
  tasks: DerivedTask[]
): string[] {
  const errors: string[] = []
  const byId = new Map(lines.map((line) => [line.id, line]))

  for (const task of tasks) {
    const source = byId.get(task.fromLineId)
    if (!source) {
      errors.push(
        `Task "${task.id}" references transcript line "${task.fromLineId}", which does not exist.`
      )
      continue
    }
    if (!source.isAction) {
      errors.push(
        `Task "${task.id}" derives from line "${task.fromLineId}", which is not marked as an action.`
      )
    }
  }

  return errors
}

if (import.meta.env?.DEV) {
  const errors = validateFixtures(sampleTranscript, derivedTasks)
  if (errors.length > 0) {
    console.error('[landing/transform] Invalid fixtures:\n' + errors.join('\n'))
  }
}

